// src/components/Iridescence.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`;

interface IridescenceProps {
  color?: [number, number, number];
  speed?: number;
  amplitude?: number;
  mouseReact?: boolean;
  className?: string;
}

const Iridescence: React.FC<IridescenceProps> = ({
  color = [0.29, 0.87, 0.51],
  speed = 1.0,
  amplitude = 0.1,
  mouseReact = true,
  className = '',
  ...rest
}) => {
  const ctnDom = useRef<HTMLDivElement>(null);
  const mousePos = useRef({ x: 0.5, y: 0.5 });
  const [isMobile, setIsMobile] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    const checkDeviceAndZoom = () => {
      setIsMobile(window.innerWidth < 768);
      const zoom = window.outerWidth / window.innerWidth;
      setZoomLevel(zoom);
    };

    checkDeviceAndZoom();
    window.addEventListener('resize', checkDeviceAndZoom);
    
    return () => {
      window.removeEventListener('resize', checkDeviceAndZoom);
    };
  }, []);

  useEffect(() => {
    if (!ctnDom.current) return;
    
    const ctn = ctnDom.current;
    const renderer = new Renderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    
    const gl = renderer.gl;
    gl.clearColor(0.0, 0.0, 0.0, 0.0);

    let program: Program;
    let isResizing = false;

    function resize() {
      if (isResizing) return;
      isResizing = true;
      
      requestAnimationFrame(() => {
        const width = ctn.offsetWidth;
        const height = ctn.offsetHeight;
        
        const pixelRatio = (window.devicePixelRatio || 1) * zoomLevel;
        const realWidth = width * pixelRatio;
        const realHeight = height * pixelRatio;
        
        renderer.setSize(realWidth, realHeight);
        
        gl.canvas.style.width = `${width}px`;
        gl.canvas.style.height = `${height}px`;
        
        if (program) {
          program.uniforms.uResolution.value = new Color(
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height
          );
        }
        
        isResizing = false;
      });
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(ctn);
    
    window.addEventListener('resize', resize);
    resize();

    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(...color) },
        uResolution: {
          value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
        },
        uMouse: { value: new Float32Array([mousePos.current.x, mousePos.current.y]) },
        uAmplitude: { value: amplitude * (isMobile ? 0.5 : 1) },
        uSpeed: { value: speed * (isMobile ? 0.8 : 1) }
      }
    });

    const mesh = new Mesh(gl, { geometry, program });
    let animateId: number;

    function update(t: number) {
      animateId = requestAnimationFrame(update);
      program.uniforms.uTime.value = t * 0.001;
      renderer.render({ scene: mesh });
    }
    animateId = requestAnimationFrame(update);
    
    gl.canvas.style.position = 'absolute';
    gl.canvas.style.top = '0';
    gl.canvas.style.left = '0';
    gl.canvas.style.width = '100%';
    gl.canvas.style.height = '100%';
    gl.canvas.style.pointerEvents = 'none';
    ctn.appendChild(gl.canvas);

    function handleMouseMove(e: MouseEvent) {
      if (!mouseReact || isMobile) return;
      
      const rect = ctn.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      mousePos.current = { x, y };
      program.uniforms.uMouse.value[0] = x;
      program.uniforms.uMouse.value[1] = y;
    }

    if (mouseReact && !isMobile) {
      ctn.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      cancelAnimationFrame(animateId);
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();
      
      if (mouseReact && !isMobile) {
        ctn.removeEventListener('mousemove', handleMouseMove);
      }
      
      if (ctn.contains(gl.canvas)) {
        ctn.removeChild(gl.canvas);
      }
      
      try {
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      } catch (e) {
        console.log('WebGL context cleanup');
      }
    };
  }, [color, speed, amplitude, mouseReact, isMobile, zoomLevel]);

  return (
    <div 
      ref={ctnDom} 
      className={`w-full h-full fixed inset-0 ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: mouseReact ? 'auto' : 'none',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
      {...rest}
    />
  );
};

export default Iridescence;